import { APIFetchError } from "@cur8/api-client";
import { useQuery } from "@tanstack/react-query";
import { APIClient } from "lib/api/client";
import { useAPIClient } from "render/context/APIContext";

// The URL expires 5 minutes after it is issued, so it is keyed by the opening
// of the view it was issued for: a new opening never starts from a cached one.
export function useCreateNekoLensAuthUrl(opening: number) {
  const apiClient = useAPIClient();

  return useQuery({
    queryKey: queryKey(opening),
    queryFn: queryFn(apiClient),
    // Nothing reads an opening's URL again once its view has closed.
    gcTime: 0,
    // One code per opening, so it never changes under a device mid-scan. The
    // view closes before the URL expires, sleep included.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // A 403 means the account has no access to Neko Lens, which no retry will
    // change. The API client already retries 5xx responses itself, so one
    // retry here covers what it doesn't, like a dropped connection.
    retry: (count, error) => !isNoNekoLensAccess(error) && count < 1,
  });
}

export function isNoNekoLensAccess(error: unknown): boolean {
  return error instanceof APIFetchError && error.response.status === 403;
}

const queryFn = (apiClient: APIClient) => async () => {
  const url = await apiClient.nekoScope.createAuthUrl().result;
  return url.substring(1, url.length - 1); // Remove quotes added by APIBackend
};
const queryKey = (opening: number) => ["nekoScopeAuthUrl", opening];
